import { getCurrencyByCode } from '@/constants/currencies'

/**
 * Money formatting — one home for turning a stored amount into a display string.
 *
 * App-wide convention: every amount is an integer in **hundredths** (the input
 * side multiplies by 100, the backend divides by 100). That `×100` scaling is
 * independent of how many fraction digits a currency actually has — which is why
 * zero-decimal currencies (JPY, KRW, CLP, …) used to render as "¥100.00" when the
 * per-file formatters hard-coded `.toFixed(2)`. Here the fraction digits come from
 * the currency itself, so a 100-hundredths yen amount renders as "¥1".
 */

const decimalsCache = new Map<string, number>()

/**
 * Fraction digits a currency uses, per ISO 4217 (JPY/KRW/CLP → 0, USD/EUR → 2,
 * BHD/KWD → 3). Sourced from `Intl` so there is no table to hand-maintain; falls
 * back to 2 for codes `Intl` does not recognize (e.g. offshore "cnh"). Memoized.
 */
export const getCurrencyDecimals = (currencyCode: string): number => {
	const key = currencyCode.toLowerCase()
	const cached = decimalsCache.get(key)
	if (cached !== undefined) return cached
	let decimals = 2
	try {
		decimals =
			new Intl.NumberFormat('en', {
				style: 'currency',
				currency: currencyCode.toUpperCase(),
			}).resolvedOptions().maximumFractionDigits ?? 2
	} catch {
		decimals = 2
	}
	decimalsCache.set(key, decimals)
	return decimals
}

/**
 * Fraction digits to actually *display* a stored amount. Amounts are stored as
 * integer hundredths app-wide (×100, currency-independent), so the real precision
 * is 2 decimals no matter what a currency's ISO 4217 minor unit is. Currencies
 * with more minor-unit digits (BHD/KWD → 3, CLF → 4) would otherwise render a
 * phantom trailing zero the storage can never populate (and silently lose true
 * sub-cent values), so we clamp the ISO count down to the 2-decimal storage
 * precision. Zero-decimal currencies (JPY/KRW → 0) are unaffected: `min(0, 2)` is 0.
 */
export const getDisplayDecimals = (currencyCode: string): number =>
	Math.min(getCurrencyDecimals(currencyCode), 2)

/**
 * Format a stored amount (integer hundredths) as a bare decimal string — no
 * symbol, no thousands separators — at the currency's displayable precision.
 * For machine-readable artifacts (CSV exports) where a spreadsheet must parse
 * the value; sign is preserved (`-3333, 'usd'` → "-33.33").
 */
export const formatMoneyPlain = (amount: number, currencyCode: string): string =>
	(amount / 100).toFixed(getDisplayDecimals(currencyCode))

/**
 * Format a stored amount (integer hundredths) with its currency symbol at the
 * currency's displayable precision (see {@link getDisplayDecimals} — capped at
 * the 2-decimal storage precision). Unknown currencies fall back to a
 * `$`-prefixed 2-decimal value, matching the prior per-file formatters. The
 * caller controls sign (pass `Math.abs(...)` for an unsigned display).
 *
 * e.g. formatMoney(4200, 'usd') → "$42.00"; formatMoney(10000, 'jpy') → "¥100".
 */
export const formatMoney = (amount: number, currencyCode: string): string => {
	const currency = getCurrencyByCode(currencyCode)
	const value = (amount / 100).toFixed(getDisplayDecimals(currencyCode))
	if (!currency) return `$${value}`
	return currency.isSymbolOnLeft ? `${currency.symbol}${value}` : `${value}${currency.symbol}`
}
